import { ulid } from 'ulid';
import State from '../Data/State';
// import UI from './UI';
import { Roles } from '../Data/Users';
import { levels } from '../Data/Admin';
import { toQueryString } from './helpers';
import { captureException } from './Sentry';
import Callback from './Callback';

const LOCAL_DOMAIN = 'http://localhost:8000/api/';
// const LOCAL_DOMAIN = 'http://192.168.1.139:8000/api/';
const NGROK_DOMAIN = 'https://gspyapi.ngrok.dev/api/';
const PRODUCTION_DOMAIN = 'https://app.getsoapy.com/api/';

export default new class API {

    domain;
    mapsDomain;

    token = null;

    confirm = null;

    onLogout = null;

    flags = '';

    constructor() {
        this.onLogout = new Callback();

        this.setDomain();
        this.getToken();

        this.sentry = import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Sentry');

        // Persist admin disabled state when switching designs
        this.setAdminDisabled(window.localStorage.getItem('adminDisabled') === 'true');
    }

    setConfirmCallback(callback) {
        this.confirm = callback;
    }

    setDomain() {
        // this.domain = PRODUCTION_DOMAIN;
        // return;

        const href = window.location.href;
        if (href.indexOf('localhost') !== -1 || href.indexOf(':3000') !== -1) {
            this.domain = LOCAL_DOMAIN;
            this.mapsDomain = LOCAL_DOMAIN;
            // this.domain = NGROK_DOMAIN;
            // this.mapsDomain = PRODUCTION_DOMAIN;
        } else if (href.indexOf('ngrok') !== -1 || href.indexOf('192.168') !== -1) {
            this.domain = NGROK_DOMAIN;
            this.mapsDomain = NGROK_DOMAIN;
        } else {
            this.domain = PRODUCTION_DOMAIN;
            this.mapsDomain = 'https://maps.getsoapy.com/api/';
        }
    }

    setFlags(flags) {
        this.flags = flags;
    }

    reset() {
        window.apiPreloads = {};
        this.domain = null;
        localStorage.removeItem('token');
        this.setDomain();
    }

    setToken(token) {
        this.token = token;
        if (token === null) {
            localStorage.removeItem('token');
        } else {
            localStorage.setItem('token', token);
        }
        if (window._sentry) {
            console.log('Setting sentry')
            window._sentry?.setUser({
                userId: this.jwt('userId'),
                businessId: this.jwt('businessId'),
            });
        }
    }

    getToken() {
        if (this.token == null && localStorage.getItem('token')) {
            this.token = localStorage.getItem('token')
        }

        return this.token;
    }

    getHeaders() {
        const requestId = ulid();

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Request-Id': requestId,
            'X-Visitor-Id': this.getCookie('visitor-id'),
            'X-Soapy-Context': this.createSoapyContextHeader(),
        };
        if (this.token) {
            headers.Authorization = 'Bearer ' + this.token
        }

        return headers;
    }

    getCookie(cname) {
        const name = cname + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');

        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }

        return null;
    }

    makeOptions(options) {
        return {
            handleConflict: true,
            ...options,
        };
    }

    get(path, params = null, options = {}) {
        const queryString = params ? '?'+toQueryString(params) : '';

        return this.call(
            path+queryString,
            {
                headers: this.getHeaders(),
                cache: 'reload',
            },
            this.makeOptions(options),
        );
    }

    post(path, data = null, options = {}) {
        return this.call(
            path,
            {
                method: 'post',
                headers: this.getHeaders(),
                cache: 'reload',
                body: data ? JSON.stringify(data) : null
            },
            this.makeOptions(options),
        );
    }

    delete(path, data = null, options = {}) {
        return this.call(
            path,
            {
                method: 'delete',
                headers: this.getHeaders(),
                cache: 'reload',
                body: data ? JSON.stringify(data) : null
            },
            this.makeOptions(options),
        );
    }

    call(path, config, options = {}) {
        // The lambda URL does not like [] in the URL
        const url = this.url(path).replace(/\[/g, '%5B').replace(/\]/g, '%5D');
        const promise = new Promise((resolve, reject) => {
            let params;

            if (window.apiPreloads[url]) {
                params = window.apiPreloads[url];
                delete window.apiPreloads[url];
            } else {
                params = fetch(url, config);
            }

            return params.then(response => this.handleResponse(
                    response, resolve, reject, {url, config}, options
                ))
                .catch(err => this.handleError(err, resolve, reject, {url, config}, options));
        });

        return promise;
    }

    download = (url) => {
        return new Promise((resolve, reject) => {
            try {
                let anchor = document.createElement("a");
                document.body.appendChild(anchor);

                let headers = new Headers();
                headers.append('Authorization', 'Bearer ' + this.token);
                let filename;

                fetch(url, { headers })
                    .then(xhr => {
                        filename = xhr.headers.get('content-disposition').split('filename=')[1].split(';')[0];
                        return xhr;
                    })
                    .then(response => response.blob())
                    .then(blobby => {
                        let objectUrl = window.URL.createObjectURL(blobby);

                        anchor.href = objectUrl;
                        anchor.download = filename;
                        anchor.click();

                        window.URL.revokeObjectURL(objectUrl);

                        resolve();
                    });
            } catch (e) {
                reject(e);
                captureException(e);
            }
        });
    }

    url(path) {
        if (path.indexOf('http') === 0) {
            return path;
        }

        return this.domain + path.replace(/^\//, '');
    }

    mapUrl(path) {
        if (path.indexOf('http') === 0) {
            return path;
        }

        return this.mapsDomain + path.replace(/^\//, '');
    }

    async handleResponse(response, resolve, reject, retry, options = {}) {
        const data = await response.json();
        if (data.success !== true) {
            const handled = this.handle401(data.errors) || this.handleConflict(data, resolve, reject, retry, options);
            if (!handled) {
                reject(data.errors);
            }
        } else {
            resolve(data.data);
        }

        const newToken = response.headers.get('soapy-authorization');
        if (newToken) {
            // console.log('New token provided, updating', newToken);
            this.setToken(newToken);
        }
    }

    async handleError(err, resolve, reject, retry, options = {}) {
        captureException(err);

        let handled = this.handle401(err) || this.handleConflict(err, resolve, reject, retry, options);

        if (handled) {
            return;
        }

        reject([
            {
                code: 0,
                title: err.title ?? err.message ?? 'Unknown error',
                detail: err.title ?? err.message ?? 'Unknown error',
            }
        ]);
    }

    parseErrorResponse(err, fallbackMessage) {
        if (typeof err === 'string') {
            return err;
        }

        if (err && err[0] && err[0].detail) {
            return err[0].detail
        }

        if (err && typeof err === 'object' && err.message) {
            return err.message;
        }

        return fallbackMessage ? fallbackMessage : 'An error has occurred'
    }

    handleConflict(data, resolve, reject, retry, options = {}) {
        if (!data.conflict || data.conflict.code !== 409 || !options.handleConflict) {
            return false;
        }

        // Make this async so we can return
        this.confirm(
            {
                theme: 'warning',
                title: data.conflict.title,
                message: data.conflict.message,
                onCancel: () => reject('Cancelled'),
            },
            () => {
                retry.config.headers['X-Conflict-Bypass-Token'] = data.conflict.bypassToken;
                fetch(retry.url, retry.config)
                    .then(response => this.handleResponse(response, resolve, reject, retry))
                    .catch(err => this.handleError(err, resolve, reject, retry));
            }
        );

        return true;
    }

    handle401(errors) {
        if (!errors || Array.isArray(errors) === false) {
            return false;
        }

        let handled = false;

        errors.forEach(err => {
            if (err.code === 401 && window.location.href.indexOf('/login') === -1) {
                console.log('HTTP 401 - Logging out');
                this.onLogout.invoke();
                handled = true;
                this.reset();
                window.location.href = '/login'
            }
        })

        return handled;
    }

    saveBusinessCookie(business) {
        const expire = new Date();
        expire.setMonth(expire.getMonth() + 12);
        const cookie = 'businessId=' + business.id + ';expires=' + expire + ';domain=getsoapy.com;path=/';
        document.cookie = cookie;
    }

    getBusinessId() {
        return this.jwt('businessId');
    }

    switchAccount(grantId) {
        return this.post('/auth/switch', { grantId }).then(res => {
            this.reset();
            this.setToken(res.token);

            window.location.replace(
                window.location.origin + '/'
            );

            return res;
        });
    }

    jwt(field, def = null) {
        if (!this.token) {
            return def;
        }

        const payload = this.token.split('.')[1];
        const json = atob(payload);
        const data = JSON.parse(json);

        return data[field] ?? def;
    }

    getRole() {
        if (window.localStorage.getItem('roleOverride')) {
            return window.localStorage.getItem('roleOverride');
        }

        return this.jwt('role', 'Owner');
    }

    isRoleSufficient(role) {
        const roles = [
            Roles.LimitedWorker,
            Roles.Worker,
            Roles.Manager,
            Roles.Owner,
        ];

        if (roles.indexOf(role) === -1) {
            return false;
        }

        return roles.indexOf(this.getRole()) >= roles.indexOf(role);
    }

    isAdmin(level = null) {
        if (State.get('admin.disabled')) {
            return false;
        }

        const currentLevel = this.getAdminLevel();

        if (!currentLevel) {
            return false;
        }

        if (level === null) {
            return true;
        }

        const adminLevels = [
            levels.EARLY_ACCESS,
            levels.READ_ONLY,
            levels.ADMIN,
            levels.SUPER,
        ];

        // If current level is great than or equal to the level we're checking, return true
        return adminLevels.indexOf(currentLevel) >= adminLevels.indexOf(level);
    }

    setAdminDisabled(change) {
        State.put('admin.disabled', !!change);
        window.localStorage.setItem('admin-disabled', change ? 'true' : 'false');
    }

    isAdminDisabled() {
        return State.get('admin.disabled');
    }

    getAdminLevel() {
        if (!this.token) {
            return window.localStorage.isAdmin
        }

        return window.localStorage.isAdmin = (this.jwt('adminSwitched') || this.jwt('isAdmin'));
    }

    switchIntoBusiness(businessId) {
        return this.get('/admin/switch?businessId=' + businessId).then(res => {
            window.localStorage.setItem('admin-token', this.token);
            this.setToken(res.token);
            window.location.href = '/';
        })
    }

    isAdminSwitchedBusiness() {
        return !!this.jwt('adminSwitched');
    }

    switchBack() {
        this.setToken(window.localStorage.getItem('admin-token'));
        window.localStorage.removeItem('admin-token');

        window.location.href = '/admin';
    }

    createSoapyContextHeader(requestId) {
        const businessId = this.jwt('businessId', 'x');
        const userId = this.jwt('sub', 'x');
        const flags = this.flags ?? '';

        const parts = [
            requestId || 'x',
            businessId || 'x',
            userId || 'x',
            typeof flags === 'string' ? flags : '',
        ];

        return parts.join('-');
    }
}()
